import { OVERLAY_DEFAULT_CONFIG, OverlayDefaultConfig } from '@angular/cdk/overlay';
import { APP_BASE_HREF } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  InjectionToken,
  importProvidersFrom,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule, routerReducer } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { CldrIntlService, IntlService } from '@progress/kendo-angular-intl';
import { MessageService } from '@progress/kendo-angular-l10n';
import { pendingRequestsInterceptor$ } from 'ng-http-loader';
import { provideDefaultClient } from 'src/api';
import { routes } from './app.routes';
import { extModules } from './build-specifics';
import { KendoMessageService } from './core/locale/kendo-message.service';
import { LocaleProvider } from './core/locale/locale.provider';
import { MultiTranslateHttpLoader } from './core/locale/translate-loader';
import { MAT_DAYJS_DATE_ADAPTER_OPTIONS } from './core/material-dayjs-adapter/dayjs-date-adapter';
import { MatDayjsDateModule } from './core/material-dayjs-adapter/material-dayjs-adapter.module';
import { webApiInterceptor } from './core/web-api-interceptor';
import { CartEffects } from './root-store/cart-store/effects';
import { reducers as cartReducers } from './root-store/cart-store/reducers';
import { reducers as catalogReducers } from './root-store/catalog-store/reducers';
import { RootStoreEffects } from './root-store/effects';
import { ObjectTreeEffects } from './root-store/object-tree-store/effects';
import { reducers as objectTreeReducers } from './root-store/object-tree-store/reducers';
import { RouterEffects } from './root-store/router/effects';
import { reducers as schedulerReducers } from './root-store/scheduler-store/reducers';
import { reducers as timePickerReducers } from './root-store/time-picker-store/reducers';
import { UserEffects } from './root-store/user-store/effects';
import { reducers as userReducers } from './root-store/user-store/reducers';

export const APP_CONFIG = new InjectionToken('app.config');

export interface IAppConfig {
  apiEndpoint: string;
  smallScreenMediaQuery: string;
}

export const APP_DI_CONFIG: IAppConfig = {
  apiEndpoint: '/api/',
  smallScreenMediaQuery: '(max-width: 1010px)',
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useClass: MultiTranslateHttpLoader,
        deps: [HttpClient],
      },
    }),
    importProvidersFrom(
      StoreModule.forRoot({}),
      StoreModule.forFeature('router', routerReducer),
      StoreModule.forFeature('user', userReducers),
      StoreModule.forFeature('catalog', catalogReducers),
      StoreModule.forFeature('cart', cartReducers),
      StoreModule.forFeature('objectTree', objectTreeReducers),
      StoreModule.forFeature('scheduler', schedulerReducers),
      StoreModule.forFeature('timePicker', timePickerReducers),
      StoreRouterConnectingModule.forRoot(),
      EffectsModule.forRoot([RouterEffects, RootStoreEffects]),
      EffectsModule.forFeature([UserEffects, CartEffects, ObjectTreeEffects]),
      extModules,
      MatDayjsDateModule, // required for Material-Datepicker
    ),
    LocaleProvider,
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'CHF',
    },
    { provide: MessageService, useClass: KendoMessageService },
    CldrIntlService,
    {
      provide: IntlService,
      useExisting: CldrIntlService,
    },
    { provide: APP_CONFIG, useValue: APP_DI_CONFIG },
    provideDefaultClient({
      basePath: '',
    }),
    { provide: APP_BASE_HREF, useValue: '/' },
    { provide: MAT_DAYJS_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    provideHttpClient(
      withXhr(),
      withInterceptors([webApiInterceptor, pendingRequestsInterceptor$]),
    ),
    // disable popover's in angular 21 because kendo popup will be hidden behind dialogs (Workaround 2026-02-13)
    // see https://github.com/telerik/kendo-angular/issues/4848
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false } satisfies OverlayDefaultConfig,
    },
    provideAnimations(),
  ],
};
